import { useCallback, useRef } from 'react'
import useWalletDetails from 'api/useWalletDetails'
import { resolveUpvalueOwnerId } from 'components/panels/ordinalUpvalueFeature'
import { sameUserId } from 'state/freeReactionEligibility'
import useStore from 'state/knovStore'
import {
    canSendFreeReactionForAnswer,
    nextPressIsFreeReaction,
    reactOrUpvalue,
    upvaluesEnabledForAnswer,
    type ReactOrUpvalueOptions,
} from 'state/upvalueQueue'
import type { Answer } from 'types/answers'
import type { SignalType } from 'types/signals'
import { BsvWalletType } from 'wallets/wallets'
import { resolveDeferredAnswerForPress } from './deferredAnswerResolution'
import { paidSignalTypeForTarget } from './signalOwnership'

type UseUpvalueActionOptions = {
    answer: Answer
    artifactOrdinalId?: string
    artifactOwnerId?: string
    source?: string
    resolveAnswer?: () => Promise<Answer | null>
}

/**
 * Shared business adapter for signal controls. UI surfaces own their layout and
 * interaction model; this hook owns authentication, deferred answer resolution,
 * wallet state, target ownership, eligibility, and the canonical signal queue.
 */
export function useUpvalueAction({
    answer,
    artifactOrdinalId,
    artifactOwnerId,
    source,
    resolveAnswer,
}: UseUpvalueActionOptions) {
    const walletType = useStore(state => state.currentUserBsvWalletType) || BsvWalletType.SHUALLET
    const walletDetails = useWalletDetails(walletType)
    const signalType = useStore(state => state.signalType)
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)
    const currentUser = gon?.currentUser as { id: string | number } | undefined
    const resolvingAnswerRef = useRef<Promise<Answer | null> | null>(null)

    const handleUpvalue = useCallback(
        async (
            sats: number,
            _blocks?: number,
            type: SignalType = 'upvalue',
            options?: ReactOrUpvalueOptions,
        ) => {
            if (!gon?.currentUser) {
                openAuthModal('signup', 'upvalue_button')
                return
            }

            try {
                const actionAnswer = resolveAnswer
                    ? await resolveDeferredAnswerForPress(resolvingAnswerRef, resolveAnswer)
                    : answer
                if (!actionAnswer) {
                    options?.onFreeReactionFailed?.(
                        new Error('Unable to resolve the answer for this artifact.'),
                    )
                    return
                }

                reactOrUpvalue(actionAnswer, sats, type, {
                    source: options?.source || source || 'quick_button',
                    artifactOrdinalId,
                    ...options,
                })
            } catch (error) {
                console.error('Error resolving answer for upvalue:', error)
                options?.onFreeReactionFailed?.(error as Error)
            }
        },
        [answer, artifactOrdinalId, openAuthModal, resolveAnswer, source],
    )

    const upvalueOwnerId = resolveUpvalueOwnerId(
        answer.user_id,
        artifactOwnerId,
        answer.artifact_owner_id,
    )
    const showBoost =
        paidSignalTypeForTarget(signalType, sameUserId(currentUser?.id, upvalueOwnerId)) === 'boost'

    return {
        currentUser,
        freeReactionAvailable: canSendFreeReactionForAnswer(answer),
        handleUpvalue,
        nextPressIsFree: nextPressIsFreeReaction(answer),
        showBoost,
        signalType,
        upvaluesEnabled: upvaluesEnabledForAnswer(answer),
        walletBalanceSats: walletDetails.data?.balanceLoaded ? walletDetails.data.balance : null,
    }
}
